import { GraffitiGuarded } from "@graffiti-garden/wrapper-data-guard";
import { GraffitiLocal } from "@graffiti-garden/implementation-local";
import { installTransclude } from "./transclude";
import { installChildBridgeEndpoints } from "./bridges/child";
import { DEFAULT_DOCUMENT_ROUTE } from "./constants";
import { createDocumentRouteState } from "./bridges/navigation/document-route";
import { serializeRouteUrl } from "./bridges/navigation/route-serialization";
import { handleNavigation } from "./bridges/navigation/shared";
import { createParentBridgeEndpointInstaller } from "./bridges/parent";
import { createPeripheralsHost } from "./bridges/peripherals/host";
import { installGuardIdentity } from "./bridges/graffiti/identity";
import {
  applyDocumentMetadata,
  isDocumentMetadata,
  DOCUMENT_METADATA_EVENT,
} from "./bridges/document-metadata/shared";
import { createPeripheralPermissions } from "./bridges/peripherals/permissions";
import { createDefaultResolver } from "./bridges/resolution/default";
import { serializeDocument } from "./bridges/resolution/document";
import {
  handleDocumentResolution,
  resolveDocument,
} from "./bridges/resolution/shared";
import { canReplaceRouteHistory, decodeUrlQuery } from "./url-route";

declare const KERNEL_IMPORT_MAP: { imports: Record<string, string> };
declare const TEST_GRAFFITI: boolean;

const currentScript = document.currentScript;
if (!(currentScript instanceof HTMLScriptElement) || !currentScript.src) {
  throw new Error("The Social.Wiki kernel must be loaded as a classic script");
}
const kernelUrl = new URL(currentScript.src);

// For most documents, which do not run at the top level...
if (window.top !== window) {
  // A classic script can inject the import map before later module scripts run.
  const importScript = document.createElement("script");
  importScript.type = "importmap";
  importScript.textContent = JSON.stringify(KERNEL_IMPORT_MAP);
  document.head.append(importScript);

  // Set up a connection to the parent document for
  // passing Graffiti data, navigation requests,
  // resolutions, and so on
  const bridgedServices = installChildBridgeEndpoints();

  // Initialize an installer that will pass on the
  // connected services to any sub documents
  const installParentBridgeEndpoints =
    createParentBridgeEndpointInstaller(bridgedServices);

  // Initialize the <sw-transclude> component that
  // allows this document to include sub-documents
  const { resolve } = bridgedServices;
  installTransclude(resolve, installParentBridgeEndpoints, kernelUrl.href);
} else {
  const guardPath = installGuardIdentity([window.location.origin]);
  // If we are the top-level document, wrap the document in an iframe
  // while this document acts as the host for all nested documents
  const initializeHost = () => {
    const documentUrl = window.location.href;
    const documentTitle = document.title;
    const html = serializeDocument(document, documentUrl);

    // Replace the document with a clean host for the root transclude below.
    document.documentElement.replaceChildren(
      document.createElement("head"),
      document.createElement("body"),
    );

    // Install top-level services: Graffiti, peripherals, resolution, and navigation
    const graffitiGuarded = TEST_GRAFFITI ? undefined : new GraffitiGuarded();
    const peripherals = createPeripheralsHost(
      undefined,
      createPeripheralPermissions(TEST_GRAFFITI ? {} : {
        dataPermissionUrl: (source) => graffitiGuarded!.auditUrl({ source }),
      }),
    );
    handleDocumentResolution(createDefaultResolver(kernelUrl.href));
    // Most pages resolve URLs relative to a default route (https://social.wiki),
    // but allow browser-like documents to override this route with their own roots
    const configuredDocumentRoute = currentScript.getAttribute(
      "data-document-route",
    );
    const rootDocumentRoute = new URL(
      configuredDocumentRoute ?? DEFAULT_DOCUMENT_ROUTE,
      documentUrl,
    );
    rootDocumentRoute.hash = "";
    const rootRoute = {
      rootUrl: rootDocumentRoute.href,
      queryRootUrl: documentUrl,
      queryPrefix: "",
    };
    const documentRoute = createDocumentRouteState(rootRoute);

    // Never let a sandboxed descendant replace the trusted host with executable
    // code, inline content, a local file, or a browser-internal document. Other
    // protocols remain available for external application handlers.
    const BLOCKED_NAVIGATION_PROTOCOLS = new Set([
      "javascript:",
      "vbscript:",
      "data:",
      "blob:",
      "filesystem:",
      "file:",
      "about:",
    ]);
    // Make an installer that allows those services to be
    // bridged to sub-documents.
    const bridgedServices = {
      guardPath,
      // Create Graffiti Local when a frame connects so the bridge observes
      // its initial session events.
      createGraffiti: () => TEST_GRAFFITI
        ? new GraffitiLocal()
        : graffitiGuarded!,
      resolve: resolveDocument,
      documentRoute,
      peripherals,
    };
    const installParentBridgeEndpoints =
      createParentBridgeEndpointInstaller(bridgedServices);

    // Install the <sw-transclude> component for including sub-documents
    installTransclude(resolveDocument, installParentBridgeEndpoints, kernelUrl.href);

    // Transclude the serialized document
    const transclude = document.createElement("sw-transclude");
    transclude.style.position = "fixed";
    transclude.style.top = "0";
    transclude.style.left = "0";
    transclude.style.width = "100dvw";
    transclude.style.height = "100dvh";
    // Keep the kernel-created source stable across reloads.
    transclude.id = "root";
    // The root frame is the origin's own document, not a separate permission
    // principal beneath it.
    transclude.setAttribute("permission-scope", "inherit");
    // This iframe does not consume any part of the route,
    // it is fully inherited by the transcluded document.
    transclude.setAttribute("route", "");
    transclude.setAttribute(
      "name",
      documentTitle || new URL(documentUrl).hostname,
    );
    transclude.setAttribute("srcdoc", html);
    transclude.addEventListener(DOCUMENT_METADATA_EVENT, (event) => {
      const metadata = (event as CustomEvent<unknown>).detail;
      if (!isDocumentMetadata(metadata)) return;
      event.preventDefault();
      applyDocumentMetadata(
        metadata,
        documentTitle || new URL(documentUrl).hostname,
      );
    });

    // Forward any changes to the route to the top-level document
    const syncRoute = () => {
      const query = decodeUrlQuery(window.location.hash) ?? "";
      if (transclude.getAttribute("query") !== query) {
        transclude.setAttribute("query", query);
      }
    };
    syncRoute();
    window.addEventListener("hashchange", syncRoute);

    handleNavigation((to) => {
      try {
        const url =
          serializeRouteUrl(to, rootRoute) ??
          new URL(to, rootRoute.rootUrl);
        if (BLOCKED_NAVIGATION_PROTOCOLS.has(url.protocol)) return;
        if (canReplaceRouteHistory(new URL(window.location.href), url)) {
          // History API updates do not fire hashchange, so deliver the new
          // query to the root transclusion directly.
          try {
            window.history.replaceState(window.history.state, "", url.href);
          } catch {
            // Preserve navigation if the browser rejects a history update.
            window.location.href = url.href;
            return;
          }
          syncRoute();
        } else {
          // A new address (or external URL) is ordinary browser navigation.
          window.location.href = url.href;
        }
      } catch {
        // Ignore malformed requests.
      }
    });

    document.body.appendChild(transclude);
  };

  if (document.readyState === "loading") {
    window.addEventListener("DOMContentLoaded", initializeHost, { once: true });
  } else {
    initializeHost();
  }
}
